/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { CertModel, L7SSLCert, SSLCipherList } from '../../../models';
import { CertsApiService, SSLCiphersApiService } from '../../../http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { LanguageService, LoadingService, LogService, ModalService, UtilsService } from '../../../../../../core/services';
import { forkJoin, Observable } from 'rxjs';
import { hostnameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ssl-offload-edit-cert',
    templateUrl: './editCert.component.html',
    styleUrls: ['./editCert.component.scss'],
})

export class EditCertComponent extends BaseComponent {
    /**
     * Store incoming data for certificate.
     */
    @Input() public certData: any;

    /**
     * Store the incoming certificates list.
     */
    @Input() public SSLCerts: L7SSLCert[] = [];

    /**
     * Emit event to close cert page.
     */
    @Output() public closeEditCertPage: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Emit the updated certificates to parent.
     */
    @Output() public certs: EventEmitter<any[]> = new EventEmitter<any[]>();

    /**
     * Form to Add/edit allow or deny hostnames.
     */
    public editHostNamesForm: FormGroup;

    /**
     * Edit cert page form.
     */
    public pageForm: FormGroup;

    /**
     * Add/edit hostname flag.
     */
    public addEditMode: boolean = false;

    /**
     * Store the type of hostname to be added.
     */
    public hostnameType: string;

    /**
     * allow rows.
     */
    public allowTableRows: any[] = [];

    /**
     * allow rows.
     */
    public denyTableRows: any[] = [];

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown = {
        ciphers: []
    };

    /**
     * An array containing certificate models of certs currently available on the LB
     * @var SSLCertificates
     */
    public SSLCertificates: CertModel[] = [];

    /**
     * @var userCipherLists
     * The array containing the cipher lists available to the userManagement
     */
    public userCipherLists: SSLCipherList[] = [];

    /**
     * Hostname form array.
     */
    public _hostnameArray: any = [];

    /**
     * Store the initial data for form comparison.
     * @private
     */
    private initialHostnamesData: any = {};

    /**
     * Store the initial page data.
     * @private
     */
    private initialPageData: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private certsApiService: CertsApiService,
        private ciphersApiService: SSLCiphersApiService,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private utilsService: UtilsService
    ) {
        super();

        this.setupPageForm();
        this.setupPageFormSubscribers();
        this.initLoading();
    }

    /**
     * Get the allow/deny hostname form depending on the mode.
     */
    get hostnameForm(): FormArray {
        const form = this.editHostNamesForm.get('hostnames');
        this._hostnameArray = form as FormArray;

        return this._hostnameArray;
    }

    get hostnameFields(): FormArray {
        return this.editHostNamesForm.get('hostnames') as FormArray;
    }

    /**
     * Delete SSL cert by row.
     * @param row
     */
    public deleteAnSSLTermination(row: any): void {
        if (this.SSLCerts.length === 1) {
            this.confirmDeleteSSLTermination(row);
        } else {
            this.removeCertificateAndUpdate(row);
        }
    }

    /**
     * Add hostname.
     * @param value
     */
    public addHostname(value?: string): void {
        this.hostnameForm.push(new FormControl((value ? value : null), [hostnameValidation]));
    }

    /**
     * Remove hostname
     * @param index
     */
    public removeHostname(index: number): void {
        this.hostnameForm.removeAt(index);
    }

    /**
     * Edit hostname
     * @param id
     * @param type
     */
    public editHostName(id: string, type: string): void {
        this.hostnameType = type;
        this.setupHostnameForms();
        this.setupHostnameFormSubscribers();
        this.patchDataToForm(id);
        this.addEditMode = true;
    }

    /**
     * Remove host from table and certs
     * @param row
     * @param type
     */
    public deleteHostname(row: any, type: string): void {
        const hostName = row.hostNames;
        const certHostNames = this.findACertById(this.certData.editId)[type];
        const index = certHostNames.indexOf(hostName);
        if (index === -1) {
            this.logService.debug(
                'sslOffloadOptions.component: deleteHostname',
                'Hostname could not be found for deletion',
                hostName
            );

            return;
        }

        certHostNames.splice(index, 1);
        this.populateTables(this.certData.editId);
        this.pageForm.get(type).setValue(certHostNames);
    }

    /**
     * Add a hostname to the configuration.
     */
    public submitHostNames(): void {
        const formValues = this.editHostNamesForm.get('hostnames').value;

        const currentCertificate: L7SSLCert = this.findACertById(this.certData.editId);
        // Get both hostname arrays and check for duplicates.
        const isValid: boolean = this.checkForDuplicates(currentCertificate, formValues);
        if (isValid) {
            this.pushHostnamesToCert(currentCertificate, formValues);
            this.populateTables(this.certData.editId);

            this.addEditMode = false;
        }
    }

    /**
     * Output the updated certificate to the parent component.
     */
    public updateCert(): void {
        this.certs.emit(this.SSLCerts);
    }

    /**
     * Cancel the add/edit.
     */
    public cancelAddEdit(): void {
        this.addEditMode = false;
        this.loadingService.loadingActionDotsEmitter.next(false);
    }

    /**
     * Return to ssl offload page.
     */
    public goBack(): void {
        this.closeEditCertPage.emit(false);
    }

    /**
     * Confirm delete user.
     * @param row
     * @private
     */
    private confirmDeleteSSLTermination(row: any): void {
        const confirmationObject = {
            title: 'ssl_offload_delete_termination_title',
            message: 'Are you sure you want to delete certificate: ' + row.name + '?',
            callBack: this.removeCertificateAndUpdate.bind(this, row),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Remove the certificate from the list and update the parent.
     * @param row
     * @private
     */
    private removeCertificateAndUpdate(row: any): Observable<any> {
        return new Observable((observer) => {
            const certToRemove: L7SSLCert = this.SSLCerts.find(i => i.certificateId === row.editId);
            const certToRemoveIndex: number = this.SSLCerts.indexOf(certToRemove);
            if (certToRemoveIndex === -1) {
                observer.error('Certificate not found');
                observer.complete();
            } else {
                this.SSLCerts.splice(certToRemoveIndex);
                this.updateCert();
                observer.next();
                observer.complete();
            }
        });
    }

    /**
     * Push hostnames to the certificate based on type.
     * @param currentCertificate
     * @param formValues
     * @private
     */
    private pushHostnamesToCert(currentCertificate: any, formValues: any): void {
        const type = this.hostnameType === 'allow' ? 'certMatchHostnames' : 'certDenyHostnames';
        currentCertificate[type] = formValues;
        this.pageForm.get(type).setValue(formValues);
    }

    /**
     * Populate the allow and deny tables.
     * @param id
     * @private
     */
    private populateTables(id: string): void {
        const currentCert: L7SSLCert = this.findACertById(id);
        const allowHostNames: string[] = currentCert.certMatchHostnames;
        const denyHostNames: string[] = currentCert.certDenyHostnames;

        this.allowTableRows = this.getTableRows(id, allowHostNames);
        this.denyTableRows = this.getTableRows(id, denyHostNames);
    }

    /**
     * Get table rows for supplied type.
     * @param id
     * @param hostnames
     * @private
     */
    private getTableRows(id: string, hostnames: string[]): any[] {
        if (hostnames.length === 0) {
            return [];
        }
        const rows = [];
        for (const name of hostnames) {
            rows.push({
                id: id,
                hostNames: name
            });
        }

        return [...rows];
    }

    /**
     * Check for duplicate entries when submitting a hostname.
     * @param currentCertificate
     * @param hostname
     * @private
     */
    private checkForDuplicates(currentCertificate: any, hostname: string): boolean {
        const allowHostNames = currentCertificate['certMatchHostnames'];
        const denyHostNames = currentCertificate['certDenyHostnames'];
        const allowIsGood: boolean = allowHostNames.indexOf(hostname) === -1;
        const denyIsGood: boolean = denyHostNames.indexOf(hostname) === -1;

        return allowIsGood && denyIsGood;
    }

    /**
     * Locate a certificate in the certs array by its ID
     * @param id
     * @private
     */
    private findACertById(id: string): L7SSLCert {
        return this.SSLCerts.find((i: L7SSLCert) => i.certificateId === id);
    }

    /**
     * Setup form for the edit cert page.
     * @private
     */
    private setupPageForm(): void {
        this.pageForm = this.formBuilder.group({
            certMatchHostnames: [],
            certDenyHostnames: [],
            cipherListId: '',
        });
    }

    /**
     * Set up the subscribers for the edit cert page form.
     * @private
     */
    private setupPageFormSubscribers(): void {
        const pageForm = this.pageForm;
        pageForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (form: any): void => {
                    this.utilsService.debounce('form', () => {
                        if (!this.utilsService.hasFormDataChanged(this.initialPageData, form)) {
                            pageForm.markAsPristine();
                        } else {
                            pageForm.markAsDirty();
                        }
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EditCertComponent: setupPageFormSubscribers - error ', error.message);
                }
            });
    }

    /**
     * Set up the form.
     * @private
     */
    private setupHostnameForms(): void {
        this.editHostNamesForm = this.formBuilder.group({
            hostnames: new FormArray([
                new FormControl(null, [hostnameValidation])
            ]),
        });
    }

    /**
     * Setup hostname form subscribers.
     * @private
     */
    private setupHostnameFormSubscribers(): void {
        const currentCert: L7SSLCert = this.findACertById(this.certData.editId);
        const editHostNamesForm = this.editHostNamesForm;
        editHostNamesForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (form: any): void => {
                    this.utilsService.debounce('form', () => {
                        if (!this.validateForm(form) && !this.hasNullValues(form)) {
                            this.checkForDuplicateHostnames(form, currentCert);
                            editHostNamesForm.markAsDirty();
                        } else {
                            editHostNamesForm.markAsPristine();
                        }
                    }, 250);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EditCertComponent: setupHostnameFormSubscribers - error ', error.message);
                }
            });
    }

    /**
     * Check for duplicate hostnames.
     * @param form
     * @param currentCert
     * @private
     */
    private checkForDuplicateHostnames(form: any, currentCert: L7SSLCert): void {
        for (let i = 0; i < this.hostnameForm.controls.length; i++) {
            const secondaryCollection = this.hostnameType === 'deny'
                ? currentCert.certDenyHostnames : currentCert.certMatchHostnames;
            const indexOfMatch = form.indexOf(this.hostnameForm.controls[i].value);
            const indexInSecondary = secondaryCollection.indexOf(this.hostnameForm.controls[i].value);
            if ((indexOfMatch !== -1 && i !== indexOfMatch) || indexInSecondary !== -1) {
                this.hostnameForm.controls[i].setErrors({ 'duplicateHostname': `${this.languageService.translate('duplicate_hostname')}` });
            }
        }
    }

    /**
     * Patch data to the form.
     * @param id
     * @private
     */
    private patchDataToForm(id: string): void {
        const currentCert: L7SSLCert = this.findACertById(id);
        this.hostnameForm.controls = [];
        const formValues = this.hostnameType === 'allow'
            ? currentCert.certMatchHostnames : currentCert.certDenyHostnames;
        this.initialHostnamesData = formValues;

        if (!formValues.length) {
            this.hostnameForm.push(new FormControl(null, [hostnameValidation]));
        } else {
            for (const value of formValues) {
                this.hostnameForm.push(new FormControl(value, [hostnameValidation]));
            }
        }
    }

    /**
     * Check if items in hostnames array are same as in initial data array.
     * @param form
     * @private
     */
    private validateForm(form: any): any {
        return form.every((_val: any, index: string | number) => this.initialHostnamesData[index]);
    }

    /**
     * Check for empty or null values.
     * @param form
     * @private
     */
    private hasNullValues(form: any): boolean {
        return (form[form.length - 1] === null) || (form[form.length - 1] === '');
    }

    /**
     * Get the certificates and cipher lists from API and set class variables
     * Once set, set up the dropdowns
     * @private
     */
    private initLoading(): void {
        forkJoin([
            this.certsApiService.getCerts(),
            this.ciphersApiService.getAllCipherLists()
        ])
            .pipe(
                takeUntil(this.destroyed$)
            )
            .subscribe({
                next: (dataGroups: any): void => {
                    this.SSLCertificates = dataGroups[0];
                    this.userCipherLists = dataGroups[1];
                    this.setUpDropdownContent();
                    this.populateTables(this.certData.editId);
                    const cert = this.findACertById(this.certData.editId);
                    this.patchDataToPageForm(cert);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EditCertComponent: initLoading - failed to fetch data. ', error.message);
                }
            });
    }

    /**
     * Setup dropdown arrays object.
     * @private
     */
    private setUpDropdownContent() {
        this.dropdown.ciphers = this.getDropDownCiphers();
    }

    /**
     * Get the dropdown values for the cipher options
     * @private
     */
    private getDropDownCiphers(): DropdownModel[] {
        const dropList = [];

        if (this.userCipherLists) {
            for (const list of this.userCipherLists) {
                dropList.push(<DropdownModel>{ key: list.id, value: list.name });
            }
        }

        return dropList;
    }

    /**
     * Patch data to page form.
     * @param cert
     * @private
     */
    private patchDataToPageForm(cert: L7SSLCert): void {
        this.initialPageData = {
            certMatchHostnames: cert.certMatchHostnames,
            certDenyHostnames: cert.certDenyHostnames,
            cipherListId: cert.cipherListId
        };

        this.pageForm.setValue(this.initialPageData);
    }
}
